import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx } from "./api";

/* ═══════════════════════════════════════════════════════════════════════════
   SUBCATEGORY FORM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const SubcategoryFormModal: React.FC<{
  open: boolean;
  subcategory: any | null;
  serviceId: number;
  categories: any[];
  onSave: (data: any) => void;
  onClose: () => void;
  saving: boolean;
}> = ({ open, subcategory, serviceId, categories, onSave, onClose, saving }) => {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [categoryId, setCategoryId] = React.useState("");

  React.useEffect(() => {
    if (subcategory) {
      setName(subcategory.name || "");
      setDescription(subcategory.description || "");
      setCategoryId(subcategory.category_id?.toString() || "");
    } else {
      setName("");
      setDescription("");
      setCategoryId(categories.length > 0 ? categories[0].id.toString() : "");
    }
  }, [subcategory, open, categories]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(139,92,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FolderOpenOutlinedIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {subcategory?.id ? "Edit Subcategory" : "New Subcategory"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          select label="Parent Category *" size="small" value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)} fullWidth sx={inputSx}
        >
          <MenuItem value="" disabled>Select category...</MenuItem>
          {categories.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <TextField
          label="Subcategory Name *" size="small" value={name}
          onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx}
        />
        <TextField
          label="Description" size="small" value={description}
          onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx}
        />
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave({
            id: subcategory?.id,
            service_id: serviceId,
            category_id: Number(categoryId),
            name: name.trim(),
            description: description || null,
          })}
          disabled={saving || !name.trim() || !categoryId}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}
        >
          {saving ? "Saving..." : "Save Subcategory"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SUBCATEGORIES SECTION
   ═══════════════════════════════════════════════════════════════════════════ */
const SubcategoriesSection: React.FC<{
  serviceId: number;
  categories: any[];
  serviceColor: string;
  onRefresh: () => void;
}> = ({ serviceId, categories, serviceColor, onRefresh }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [formOpen, setFormOpen] = useState(false);
  const [editSub, setEditSub] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Flatten all subcategories from all categories
  const allSubcategories = useMemo(() => {
    const subs: any[] = [];
    (categories ?? []).forEach((cat: any) => {
      (cat.subcategories ?? []).forEach((sub: any) => {
        subs.push({ ...sub, category_name: cat.name, category_id: cat.id });
      });
    });
    return subs;
  }, [categories]);

  const handleSave = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/subcategory", data);
    if (res.code === 200) {
      showMessage(data.id ? "Subcategory updated" : "Subcategory created");
      setFormOpen(false);
      setEditSub(null);
      onRefresh();
    } else showErrorMessage(res.message || "Failed to save");
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await api.del(`/banquet/service-tree/subcategory/${deleteTarget.id}`);
    if (res.code === 200) { showMessage("Subcategory deleted"); onRefresh(); }
    else showErrorMessage(res.message);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const thSx = { fontWeight: 700, fontSize: 11, color: T.slate, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 } as const;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FolderOpenOutlinedIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}>Subcategories</Typography>
          <Chip label={allSubcategories.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: "#8b5cf612", color: "#8b5cf6" }} />
        </Box>
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => { setEditSub(null); setFormOpen(true); }}
          disabled={categories.length === 0}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: "#8b5cf6", bgcolor: "#8b5cf608", borderRadius: "8px", px: 1.5, "&:hover": { bgcolor: "#8b5cf612" } }}>
          Add Subcategory
        </Button>
      </Box>

      {allSubcategories.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: "center", borderRadius: "12px", border: `1px dashed ${T.line}` }}>
          <FolderOpenOutlinedIcon sx={{ fontSize: 32, color: T.slateLt, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: T.slateLt }}>
            {categories.length === 0 ? "Add a category first, then create subcategories." : "No subcategories yet. Add one to further organize items."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${T.line}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: T.bg }}>
                <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                <TableCell sx={thSx}>Subcategory</TableCell>
                <TableCell sx={thSx}>Parent Category</TableCell>
                <TableCell align="center" sx={thSx}>Items</TableCell>
                <TableCell align="right" sx={thSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allSubcategories.map((sub: any, idx: number) => (
                <TableRow key={sub.id} hover sx={{ "&:hover": { bgcolor: "#fef6ee" } }}>
                  <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slateLt }}>{idx + 1}</TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FolderOpenOutlinedIcon sx={{ fontSize: 14, color: "#8b5cf6" }} />
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{sub.name}</Typography>
                        {sub.description && <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.2 }} noWrap>{sub.description}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: serviceColor }} />
                      <Typography sx={{ fontSize: 12, color: T.slate }}>{sub.category_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.2 }}>
                    <Chip label={(sub.items ?? []).length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.2 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditSub(sub); setFormOpen(true); }} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                        <EditOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteTarget({ id: sub.id, name: sub.name })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SubcategoryFormModal open={formOpen} subcategory={editSub} serviceId={serviceId} categories={categories}
        onSave={handleSave} onClose={() => { setFormOpen(false); setEditSub(null); }} saving={saving} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Delete subcategory?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Delete <strong>{deleteTarget?.name}</strong> and all its items?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubcategoriesSection;
